import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchSelect } from './SearchSelect';
import { toast } from 'sonner';

const emptyForm = { title: '', description: '', priority: 'normal', category: 'task', customer_id: '', assigned_to: [], deadline: '' };

export const TicketCreateDialog = ({ open, onOpenChange, prefill = {}, onCreated }) => {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    setFormData({
      ...emptyForm,
      title: prefill.title || '',
      description: prefill.description || '',
      customer_id: prefill.customer_id || '',
    });
    (async () => {
      try {
        const [u, c] = await Promise.all([api.get('/users'), api.get('/customers')]);
        setUsers(u.data || []);
        setCustomers(c.data || []);
      } catch (e) { /* stil */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titel is verplicht');
      return;
    }
    setSaving(true);
    try {
      await api.post('/tickets', {
        ...formData,
        customer_id: formData.customer_id || null,
        deadline: formData.deadline || null,
      });
      toast.success('Ticket aangemaakt');
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error('Kon ticket niet aanmaken');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ticket-create-dialog">
        <DialogHeader>
          <DialogTitle>Nieuw Ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titel *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Korte beschrijving van het ticket"
              data-testid="ticket-title-input"
            />
          </div>
          <div>
            <Label>Beschrijving</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Uitgebreide beschrijving..."
              rows={3}
              data-testid="ticket-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prioriteit</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Laag</SelectItem>
                  <SelectItem value="normal">Normaal</SelectItem>
                  <SelectItem value="high">Hoog</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="problem">Probleem</SelectItem>
                  <SelectItem value="question">Vraag</SelectItem>
                  <SelectItem value="memo">Memo</SelectItem>
                  <SelectItem value="task">Taak</SelectItem>
                  <SelectItem value="other">Anders</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Klant</Label>
            <SearchSelect
              options={[...customers]
                .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'nl'))
                .map(c => ({
                  value: c.id,
                  label: c.company_name
                    ? `${c.company_name} — ${c.first_name} ${c.last_name}`
                    : `${c.first_name} ${c.last_name}`,
                }))}
              value={formData.customer_id}
              onChange={(cid) => setFormData(prev => ({ ...prev, customer_id: cid || '' }))}
              placeholder="Zoek klant op naam of bedrijf..."
              className="mt-1"
            />
          </div>
          <div>
            <Label>Toegewezen aan (meerdere mogelijk)</Label>
            <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-0.5 mt-1" data-testid="ticket-assignees-list">
              {[...users]
                .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'nl'))
                .map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1.5">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={formData.assigned_to.includes(u.id)}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      assigned_to: e.target.checked
                        ? [...prev.assigned_to, u.id]
                        : prev.assigned_to.filter(uid => uid !== u.id)
                    }))}
                  />
                  {u.first_name} {u.last_name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Deadline (optioneel)</Label>
            <Input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="create-ticket-submit-btn">
            {saving ? 'Bezig...' : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TicketCreateDialog;
